import React from 'react';
import { ChevronUp } from 'lucide-react';

interface DetailedPricingTableProps {
  onToggleView: () => void;
}

const DetailedPricingTable = ({ onToggleView }: DetailedPricingTableProps) => {
  return (
    <div className="mb-8 flex justify-center">
      <div className="max-w-5xl w-full">
        <div className="flex justify-center mb-8">
          <button
            onClick={onToggleView}
            className="py-3 px-6 border border-brand-blue text-brand-blue rounded-lg font-medium hover:bg-brand-blue/5 transition-colors text-center flex items-center justify-center gap-2"
          >
            See short comparison
            <ChevronUp className="h-4 w-4" />
          </button>
        </div>
        
        <div className="glass-panel max-w-5xl mx-auto overflow-hidden">
          <div className="grid grid-cols-3">
            {/* Empty header cell */}
            <div className="p-6 border-b border-r border-gray-100"></div>
            
            {/* Sourcing Only header */}
            <div className="p-6 text-center border-b border-r border-gray-100 bg-brand-blue/5 relative pt-8">
              <div className="absolute top-0 right-0 left-0 flex justify-center">
                <span className="bg-brand-blue text-white text-xs font-semibold px-3 py-1 rounded-b-lg">
                  POPULAR
                </span>
              </div>
              <h3 className="text-xl font-semibold text-brand-darkgrey">Sourcing Only</h3>
            </div>
            
            {/* Full Recruiting header */}
            <div className="p-6 text-center border-b border-gray-100">
              <h3 className="text-xl font-semibold text-brand-darkgrey">Full Recruiting</h3>
            </div>
          </div>
          
          {/* What it does */}
          <div className="grid grid-cols-3 border-b border-gray-100">
            <div className="p-6 border-r border-gray-100 bg-brand-offwhite/30 flex items-center">
              <h4 className="font-semibold text-brand-darkgrey">What it does</h4>
            </div>
            
            <div className="p-6 border-r border-gray-100 bg-brand-blue/5">
              <ul className="space-y-3">
                <li className="flex items-start">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-check-circle h-5 w-5 text-brand-blue mr-2 mt-0.5 flex-shrink-0"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                  <span>Sources qualified candidates</span>
                </li>
              </ul>
            </div>
            
            <div className="p-6">
              <ul className="space-y-3">
                <li className="flex items-start">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-check-circle h-5 w-5 text-brand-blue mr-2 mt-0.5 flex-shrink-0"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                  <span>Sources qualified candidates</span>
                </li>
                <li className="flex items-start">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-check-circle h-5 w-5 text-brand-blue mr-2 mt-0.5 flex-shrink-0"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                  <span>Schedules and manages interviews</span>
                </li>
                <li className="flex items-start">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-check-circle h-5 w-5 text-brand-blue mr-2 mt-0.5 flex-shrink-0"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                  <span>Guides through offer process</span>
                </li>
              </ul>
            </div>
          </div>
          
          {/* Candidate pool (renamed from Data sources) */}
          <div className="grid grid-cols-3 border-b border-gray-100">
            <div className="p-6 border-r border-gray-100 bg-brand-offwhite/30 flex items-center">
              <h4 className="font-semibold text-brand-darkgrey">Candidate pool</h4>
            </div>
            
            <div className="p-6 border-r border-gray-100 bg-brand-blue/5">
              <ul className="space-y-3">
                <li className="flex items-start">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-check-circle h-5 w-5 text-brand-blue mr-2 mt-0.5 flex-shrink-0"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                  <span>3.5M registered CutShort candidates</span>
                </li>
                <li className="flex items-start">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-check-circle h-5 w-5 text-brand-blue mr-2 mt-0.5 flex-shrink-0"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                  <span>600M profiles on public internet</span>
                </li>
              </ul>
            </div>
            
            <div className="p-6">
              <ul className="space-y-3">
                <li className="flex items-start">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-check-circle h-5 w-5 text-brand-blue mr-2 mt-0.5 flex-shrink-0"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                  <span>3.5M registered CutShort candidates</span>
                </li>
                <li className="flex items-start">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-check-circle h-5 w-5 text-brand-blue mr-2 mt-0.5 flex-shrink-0"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                  <span>600M profiles on public internet</span>
                </li>
              </ul>
            </div>
          </div>
          
          {/* Who is it for */}
          <div className="grid grid-cols-3 border-b border-gray-100">
            <div className="p-6 border-r border-gray-100 bg-brand-offwhite/30 flex items-center">
              <h4 className="font-semibold text-brand-darkgrey">Who is it for</h4>
            </div>
            
            <div className="p-6 border-r border-gray-100 bg-brand-blue/5">
              <p className="text-sm text-brand-grey">
                Mature companies with established recruiting teams processes
              </p>
            </div>
            
            <div className="p-6">
              <p className="text-sm text-brand-grey">
                For any company looking for full recruiting support
              </p>
            </div>
          </div>
          
          {/* Pricing */}
          <div className="grid grid-cols-3 border-b border-gray-100">
            <div className="p-6 border-r border-gray-100 bg-brand-offwhite/30 flex items-center">
              <h4 className="font-semibold text-brand-darkgrey">Pricing</h4>
            </div>
            
            <div className="p-6 border-r border-gray-100 bg-brand-blue/5">
              <div className="mb-4">
                <span className="text-sm text-brand-grey mb-1 block">Flat fee of</span>
                <span className="text-2xl font-bold text-brand-darkgrey">3.5%</span>
                <span className="text-brand-grey ml-1">of CTC</span>
              </div>
              <p className="text-xs text-brand-grey mb-4 italic">Not a fan of % based pricing? Fixed price options are also available</p>
            </div>
            
            <div className="p-6 relative">
              <div className="mb-2">
                <span className="text-sm text-brand-grey">Starts at just</span>
              </div>
              <div className="mb-4">
                <span className="text-2xl font-bold text-brand-darkgrey">8.33%</span>
                <span className="text-brand-grey ml-1">of CTC</span>
              </div>
              <p className="text-xs text-brand-grey mt-2 italic">{"For salaries < 25LPA. Else 8.33% of CTC flat."}</p>
            </div>
          </div>
          
          {/* Common CTAs row */}
          <div className="grid grid-cols-1">
            <div className="p-8 bg-brand-offwhite/30 flex flex-col md:flex-row items-center justify-center gap-6">
              <a 
                href="https://cutshort.io/a/generic-emp-form" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-full md:w-auto py-3 px-6 bg-brand-blue text-white rounded-lg font-medium hover:bg-brand-blue/90 transition-colors shadow-md text-center"
              >
                Get a callback
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DetailedPricingTable;
